import { useState, useRef } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerDescription,
} from '@/components/ui/drawer';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useCreatePost } from '@/lib/hooks/usePosts';
import { useAuth } from '@/lib/hooks/useAuth';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { ImageIcon, X } from '@phosphor-icons/react';
import { validateImageFile, compressImage } from '@/lib/utils/image.utils';
import { uploadImage } from '@/lib/services/upload';

type CreatePostDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CreatePostDrawer({
  open,
  onOpenChange,
}: CreatePostDrawerProps) {
  const [content, setContent] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const createPost = useCreatePost();
  const { toast, showToast, hideToast } = useToastNotification();

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate the file
    const validationError = validateImageFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setUploadError(null);
    setSelectedImage(file);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setSelectedImage(null);
    setImagePreview(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (!content.trim() || !user?.id) {
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      let imageUrl: string | null = null;

      // Upload image if one is selected
      if (selectedImage && user.id) {
        try {
          // Compress the image
          const compressedBlob = await compressImage(selectedImage);

          // Upload to Supabase Storage
          imageUrl = await uploadImage(
            compressedBlob,
            user.id,
            selectedImage.name
          );
        } catch (error) {
          setUploadError(
            error instanceof Error ? error.message : 'Failed to upload image'
          );
          setIsUploading(false);
          return;
        }
      }

      // Create the post
      await createPost.mutateAsync({
        userId: user.id,
        content: content.trim(),
        imageUrl,
      });

      // Show success toast
      showToast({
        title: 'Post submitted for approval',
        description:
          'Your post has been submitted and is pending admin approval.',
        variant: 'success',
      });

      // Reset form and close drawer on success
      setContent('');
      handleRemoveImage();
      onOpenChange(false);
    } catch (error) {
      // Error handling is done by TanStack Query
      console.error('Failed to create post:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Clean up preview URL when component unmounts or drawer closes
  const handleDrawerClose = (open: boolean) => {
    if (!open && imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    onOpenChange(open);
  };

  const isSubmitting = createPost.isPending || isUploading;
  const canSubmit = content.trim().length > 0 && !isSubmitting && !!user?.id;

  return (
    <Drawer open={open} onOpenChange={handleDrawerClose}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>New Post</DrawerTitle>
          <DrawerDescription>
            Please be mindful of posting as this feed is accessible to all RISE
            cohorts and alumni.
          </DrawerDescription>
        </DrawerHeader>
        <div className="py-4">
          <Textarea
            placeholder="What's on your mind?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={isSubmitting}
            className="min-h-[120px] resize-none"
          />

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            onChange={handleImageSelect}
            className="hidden"
            disabled={isSubmitting}
          />

          {/* Upload error message */}
          {uploadError && (
            <p className="text-sm text-red-400 my-2">{uploadError}</p>
          )}

          {/* Image actions row */}
          {imagePreview ? (
            /* Compact thumbnail preview */
            <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-600 my-4">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                disabled={isSubmitting}
                className="absolute -top-1 -right-1 p-1 bg-slate-900 rounded-full shadow-md"
                aria-label="Remove image"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          ) : (
            /* Add image button - only shown when no preview */
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleImageButtonClick}
              disabled={isSubmitting}
              className="text-slate-400 my-6 hover:bg-transparent hover:text-slate-50 w-full text-center"
            >
              <ImageIcon size={20} weight="bold" />
              Add image
            </Button>
          )}
        </div>
        <DrawerFooter>
          <div className="flex gap-3">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="flex-1 h-14"
            >
              {isSubmitting
                ? isUploading
                  ? 'Uploading...'
                  : 'Posting...'
                : 'Post'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </Drawer>
  );
}
